import { defineStore } from 'pinia'
import {
  LoginRequiredError,
  type User as WorkOSUser,
  createClient,
} from '@workos-inc/authkit-js'
import * as Sentry from '@sentry/vue'

export const useAuthStore = defineStore('auth', () => {
  const { $authkit } = useNuxtApp() as unknown as {
    $authkit: Awaited<ReturnType<typeof createClient>>
  }

  const authUser = useState<WorkOSUser | null>('workos:user', () => null)

  const isAuthenticated = computed(() => !!authUser.value)

  const authServiceId = computed(() => authUser.value?.id)

  async function signIn(returnTo: string = '/') {
    await $authkit.signIn({
      state: {
        returnTo,
      },
    })
  }

  function signOut(returnTo?: string) {
    authUser.value = null

    $authkit.signOut(returnTo ? { returnTo } : undefined)
  }

  async function getToken() {
    if (!isAuthenticated.value) {
      let signInPath = '/signin'

      if (window.location.pathname !== '/') {
        signInPath += `?returnTo=${encodeURIComponent(window.location.pathname)}`
      }

      window.location.href = signInPath

      return
    }

    try {
      const response = await $authkit.getAccessToken()
      authUser.value = $authkit.getUser()

      return response
    } catch (error: any) {
      Sentry.captureException(error, {
        tags: {
          flow: 'getToken',
        },
      })

      if (error instanceof LoginRequiredError) {
        signOut(window.location.pathname)

        return
      }

      showError(error)
    }
  }

  return {
    isAuthenticated,
    authUser,
    authServiceId,
    getToken,
    signIn,
    signOut,
  }
})
